import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Tag, X, ShoppingCart } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Form, FormField, FormItem, FormControl, FormMessage,
} from '@/components/ui/form';
import { couponSchema } from '../utils/validation';
import { formatCurrency } from '../utils/formatters';

/**
 * Order Summary panel — independent of payment method logic.
 * @param {{
 *   items: import('../types/index').OrderItem[],
 *   subtotal: number,
 *   tax: number,
 *   discount: number,
 *   total: number,
 *   couponCode: string|null,
 *   couponLabel: string|null,
 *   onApplyCoupon: (data: {couponCode: string}) => void,
 *   onRemoveCoupon: () => void,
 *   isCouponLoading: boolean,
 * }} props
 */
export function OrderSummary({
    items = [],
    subtotal = 0,
    tax = 0,
    discount = 0,
    total = 0,
    couponCode = null,
    couponLabel = null,
    onApplyCoupon,
    onRemoveCoupon,
    isCouponLoading = false,
}) {
    const couponForm = useForm({
        resolver: zodResolver(couponSchema),
        defaultValues: { couponCode: '' },
        mode: 'onChange',
    });

    return (
        <Card className="border-slate-200 rounded-sm shadow-sm" data-testid="order-summary">
            <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-bold text-slate-900 font-['Outfit']">
                    <ShoppingCart className="w-5 h-5 text-[#F87217]" aria-hidden="true" />
                    Order Summary
                </CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
                {/* Line items */}
                {items.length > 0 ? (
                    <ul className="space-y-3" aria-label="Order items">
                        {items.map((item) => (
                            <li key={item.id} className="flex items-center justify-between text-sm">
                                <div className="flex-1 min-w-0">
                                    <span className="text-slate-700 font-medium truncate block">{item.name}</span>
                                    {item.quantity > 1 && (
                                        <span className="text-slate-400 text-xs">× {item.quantity}</span>
                                    )}
                                </div>
                                <span className="text-slate-900 font-semibold ml-4 flex-shrink-0">
                                    {formatCurrency(item.price * item.quantity)}
                                </span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-sm text-slate-400 italic">No items</p>
                )}

                <Separator />

                {/* Subtotal */}
                <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                        <dt className="text-slate-600">Subtotal</dt>
                        <dd className="text-slate-900 font-medium">{formatCurrency(subtotal)}</dd>
                    </div>

                    {discount > 0 && (
                        <div className="flex justify-between text-green-600">
                            <dt className="flex items-center gap-1">
                                <Tag className="w-3 h-3" aria-hidden="true" />
                                Discount
                            </dt>
                            <dd className="font-medium">− {formatCurrency(discount)}</dd>
                        </div>
                    )}


                </dl>

                <Separator />

                {/* Total */}
                <div className="flex justify-between items-center">
                    <span className="text-base font-bold text-slate-900 font-['Outfit']">Total</span>
                    <span className="text-xl font-bold text-[#F87217]" aria-live="polite">
                        {formatCurrency(total)}
                    </span>
                </div>

                <Separator />

                {/* Coupon */}
                <div>
                    {couponCode ? (
                        <div
                            className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-sm"
                            data-testid="applied-coupon"
                        >
                            <div className="flex items-center gap-2 text-sm">
                                <Tag className="w-4 h-4 text-green-600" aria-hidden="true" />
                                <span className="text-green-700 font-medium">{couponLabel || couponCode}</span>
                            </div>
                            <button
                                onClick={onRemoveCoupon}
                                className="text-green-600 hover:text-green-800 transition-colors"
                                aria-label="Remove coupon"
                                data-testid="remove-coupon"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        <Form {...couponForm}>
                            <form
                                onSubmit={couponForm.handleSubmit(onApplyCoupon)}
                                noValidate
                                className="flex gap-2"
                                data-testid="coupon-form"
                                aria-label="Apply coupon code"
                            >
                                <FormField
                                    control={couponForm.control}
                                    name="couponCode"
                                    render={({ field }) => (
                                        <FormItem className="flex-1">
                                            <FormControl>
                                                <Input
                                                    id="couponCode"
                                                    placeholder="Enter coupon code"
                                                    className="uppercase text-sm"
                                                    data-testid="input-coupon"
                                                    aria-label="Coupon code"
                                                    {...field}
                                                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <Button
                                    type="submit"
                                    disabled={isCouponLoading}
                                    variant="outline"
                                    className="border-[#F87217] text-[#F87217] hover:bg-[#F87217]/5 rounded-sm flex-shrink-0"
                                    data-testid="apply-coupon"
                                >
                                    {isCouponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                                </Button>
                            </form>
                        </Form>
                    )}
                </div>

                {/* Security badge */}
                <p className="text-xs text-slate-400 text-center pt-2">
                    🔐 256-bit SSL encrypted checkout
                </p>
            </CardContent>
        </Card>
    );
}

export default OrderSummary;
